// Landing pages for the links sent in account emails:
//   /activate/:token  — confirms the email after sign up
//   /reset/:token     — sets a new password from a reset link
// Backend ACTIVATION_LINK / RESET_LINK env vars must point at these routes.
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { activateAccount, resetPasswordFinish } from '@genfun/core/api/auth';
import './AuthLanding.css';

function Card({ title, children }) {
    return (
        <div className="d33p-auth-landing">
            <div className="d33p-auth-card">
                <h1>{title}</h1>
                {children}
            </div>
        </div>
    );
}

export function ActivateLanding() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // loading | ok | error
    const [message, setMessage] = useState('Confirming your email…');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [data, code] = await activateAccount(token);
            if (cancelled) return;
            if (code === 200) {
                setStatus('ok');
                if (data?.access_token) {
                    window.location.assign('/');
                    return;
                }
                setMessage(data?.message || 'Email confirmed. You can now log in.');
            } else {
                setStatus('error');
                setMessage(data?.detail || 'Activation failed. The link may have expired.');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    return (
        <Card title={status === 'error' ? 'Activation failed' : 'Account activation'}>
            <p className={`d33p-auth-msg${status === 'error' ? ' error' : ''}`}>{message}</p>
            {status !== 'loading' && (
                <button className="d33p-auth-btn" onClick={() => navigate('/')}>
                    Go to D33P
                </button>
            )}
        </Card>
    );
}

export function ResetLanding() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await resetPasswordFinish(token, password);
            if (code === 200) {
                setDone(true);
            } else {
                setError(data?.detail || 'Could not reset password. The link may have expired.');
            }
        } catch {
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <Card title="Password updated">
                <p className="d33p-auth-msg">Your password has been changed. You can now log in.</p>
                <button className="d33p-auth-btn" onClick={() => navigate('/')}>Go to D33P</button>
            </Card>
        );
    }

    return (
        <Card title="Set a new password">
            <p className="d33p-auth-msg">Enter a new password for your account.</p>
            <input
                type="password"
                className="d33p-auth-input"
                placeholder="New password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !loading) submit(); }}
            />
            {error && <p className="d33p-auth-msg error">{error}</p>}
            <button className="d33p-auth-btn" onClick={submit} disabled={loading}>
                {loading ? 'Please wait…' : 'Save password'}
            </button>
        </Card>
    );
}
